import { MessageSquarePlusIcon, MessageSquareTextIcon } from 'lucide-react'
import { type ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { OptionButton } from '@/components/molecules/option-button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import type { CheckpointQuestion } from '@/utils/checkpoint.utils'
import type { QuestionAnswer } from './checkpoint-question.utils'
import { PhotoCapture } from './photo-capture'

type Option = NonNullable<CheckpointQuestion['options']>[number]

export type FieldProps = {
  question: CheckpointQuestion
  answer: QuestionAnswer
  onChange(answer: QuestionAnswer): void
}

export const QuestionFieldShell = ({
  question,
  answer,
  onChange,
  layout,
  children,
}: FieldProps & { layout: 'inline' | 'stacked'; children: ReactNode }) => {
  const { t } = useTranslation()
  const hasComment = !!answer.comment
  const [showComment, setShowComment] = useState(() => hasComment)

  useEffect(() => {
    if (hasComment) setShowComment(true)
  }, [hasComment])

  const canComment = question.canLeaveComment !== false

  const label = (
    <span className={cn('text-sm font-medium', layout === 'inline' && 'flex-1 min-w-0')}>
      {question.text}
      {question.required && <span className="text-error ml-1">*</span>}
    </span>
  )

  const toggle = canComment && (
    <CommentToggle
      hasComment={hasComment}
      open={showComment}
      onClick={() => setShowComment(!showComment)}
    />
  )

  return (
    <div
      className={cn(
        'border-b border-subtle-01 last:border-b-0',
        layout === 'inline' ? 'py-2.5' : 'py-3',
      )}
    >
      {layout === 'inline' ? (
        <div className="flex items-center gap-2">
          {label}
          {children}
          {toggle}
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-2">
            {label}
            {toggle}
          </div>
          <div className="mt-2">{children}</div>
        </>
      )}

      {canComment && showComment && (
        <div className="mt-2">
          <Textarea
            className="bg-field-2 border-border-strong-2"
            placeholder={t('checkpointQuestion.commentPlaceholder')}
            value={answer.comment ?? ''}
            onChange={e => onChange({ ...answer, comment: e.target.value })}
            rows={2}
          />
        </div>
      )}
    </div>
  )
}

export const GroupHeader = ({ text }: { text: string }) => (
  <div className="pt-4 pb-1 mt-2 first:mt-0">
    <h3 className="text-xs uppercase tracking-wide text-text-secondary font-semibold">{text}</h3>
  </div>
)

export const TextAreaQuestion = ({ question, answer, onChange }: FieldProps) => {
  const { t } = useTranslation()
  const text = answer.value[0] ?? ''
  return (
    <QuestionFieldShell question={question} answer={answer} onChange={onChange} layout="stacked">
      <Textarea
        className="bg-field-2 border-border-strong-2"
        placeholder={t('checkpointQuestion.answerPlaceholder')}
        value={text}
        onChange={e => onChange({ ...answer, value: e.target.value ? [e.target.value] : [] })}
        rows={4}
      />
    </QuestionFieldShell>
  )
}

export const ImagesQuestion = ({ question, answer, onChange }: FieldProps) => (
  <QuestionFieldShell question={question} answer={answer} onChange={onChange} layout="stacked">
    <PhotoCapture
      photos={answer.files}
      onChange={files => onChange({ ...answer, files })}
      existingImages={answer.existingImages}
      onRemoveExisting={image =>
        onChange({
          ...answer,
          existingImages: (answer.existingImages ?? []).filter(i => i.path !== image.path),
        })
      }
    />
  </QuestionFieldShell>
)

export const SelectChoiceQuestion = ({ question, answer, onChange }: FieldProps) => {
  const { t } = useTranslation()
  return (
    <QuestionFieldShell question={question} answer={answer} onChange={onChange} layout="inline">
      <Select
        value={answer.value[0] ?? ''}
        onValueChange={v => onChange({ ...answer, value: [v] })}
      >
        <SelectTrigger className="w-40 shrink-0">
          <SelectValue placeholder={t('checkpointQuestion.selectPlaceholder')} />
        </SelectTrigger>
        <SelectContent>
          {(question.options ?? []).map(opt => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.text}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </QuestionFieldShell>
  )
}

export const MultiChoiceQuestion = ({ question, answer, onChange }: FieldProps) => {
  const options = question.options ?? []
  const toggle = (opt: Option) =>
    onChange({
      ...answer,
      value: answer.value.includes(opt.value)
        ? answer.value.filter(v => v !== opt.value)
        : [...answer.value, opt.value],
    })

  return (
    <QuestionFieldShell question={question} answer={answer} onChange={onChange} layout="stacked">
      <div className="grid grid-cols-2 gap-3">
        {options.map(opt => {
          const checked = answer.value.includes(opt.value)
          return (
            <label
              key={opt.value}
              data-checked={checked}
              className="group flex items-center gap-3 rounded-lg border border-strong-01 p-3 cursor-pointer transition-colors hover:bg-field-2 data-[checked=true]:border-primary data-[checked=true]:bg-primary/5"
            >
              <Checkbox checked={checked} onCheckedChange={() => toggle(opt)} />
              <span className="text-sm font-medium">{opt.text}</span>
            </label>
          )
        })}
      </div>
    </QuestionFieldShell>
  )
}

export const SingleChoiceQuestion = ({ question, answer, onChange }: FieldProps) => {
  const options = question.options ?? []
  return (
    <QuestionFieldShell question={question} answer={answer} onChange={onChange} layout="inline">
      <div className="flex gap-2 shrink-0">
        {options.map(opt => (
          <OptionButton
            key={opt.value}
            text={opt.text}
            isNotRelevant={!!opt.isNotRelevant}
            selected={answer.value.includes(opt.value)}
            onClick={() => onChange({ ...answer, value: [opt.value] })}
          />
        ))}
      </div>
    </QuestionFieldShell>
  )
}

const CommentToggle = ({
  hasComment,
  open,
  onClick,
}: {
  hasComment: boolean
  open: boolean
  onClick(): void
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'h-10 w-14 rounded-lg border-2 flex items-center justify-center transition-all shrink-0',
      hasComment
        ? 'border-success bg-success/10 text-success'
        : open
          ? 'border-info/40 bg-field-2 text-info'
          : 'border-strong-01 bg-field-2 text-text-secondary hover:bg-field-2-hover hover:border-info/40',
    )}
  >
    {hasComment ? (
      <MessageSquareTextIcon className="size-4" />
    ) : (
      <MessageSquarePlusIcon className="size-4" />
    )}
  </button>
)
